import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import api, { CATEGORY_LABELS, BRANDS, TYRE_BRANDS } from "../lib/api";
import ListingCard from "../components/ListingCard";
import Seo from "../components/Seo";

const CATS = [
  { key: "", label: "Vše" },
  { key: "kola", label: "Alu kola" },
  { key: "kusovky", label: "Kusovky (1–3 ks)" },
  { key: "pneu", label: "Pneumatiky" },
  { key: "poklicky", label: "Pokličky" },
  { key: "dily", label: "Autodíly" },
];

const SIZES = ["15", "16", "17", "18", "19", "20", "21", "22", "23"];

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const brand = params.get("brand") || "";
  const size = params.get("size") || "";
  const [listings, setListings] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (category) p.category = category;
    if (brand) p.brand = brand;
    if (size) p.size = size;
    if (q) p.q = q;
    const t = setTimeout(() => {
      api.get("/listings", { params: p })
        .then((r) => setListings(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [category, brand, size, q]);

  const setCategory = (key) => {
    const np = new URLSearchParams(params);
    if (key) np.set("category", key);
    else np.delete("category");
    if (key !== "pneu") np.delete("size");
    if (key === "pneu") np.delete("brand");
    setParams(np);
  };

  const setBrand = (val) => {
    const np = new URLSearchParams(params);
    if (val) np.set("brand", val);
    else np.delete("brand");
    setParams(np);
  };

  const setSize = (val) => {
    const np = new URLSearchParams(params);
    if (val) np.set("size", val);
    else np.delete("size");
    setParams(np);
  };

  return (
    <div className="pt-32 pb-24 relative z-10 min-h-screen">
      <Seo title={category ? `${CATEGORY_LABELS[category] || "Katalog"} — katalog` : "Katalog — alu kola, pneumatiky a autodíly"} />
      <div className="max-w-[1500px] mx-auto px-5 md:px-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="font-head uppercase tracking-[0.3em] text-xs text-[#ff3b30] mb-4">Nabídka</div>
          <h1 className="font-display text-5xl md:text-8xl leading-[0.85] mb-10">
            {size ? `R${size}″` : brand ? brand.toUpperCase() : category ? CATEGORY_LABELS[category].toUpperCase() : "KATALOG"}
          </h1>
        </motion.div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row gap-5 justify-between mb-12 border-b border-white/10 pb-8">
          <div className="flex flex-wrap gap-2">
            {CATS.map((c) => (
              <button
                key={c.key}
                data-testid={`filter-cat-${c.key || "all"}`}
                onClick={() => setCategory(c.key)}
                className={`font-head uppercase tracking-wider text-sm px-5 py-2.5 border transition-colors duration-300 ${
                  category === c.key
                    ? "bg-[#ff3b30] border-[#ff3b30] text-white"
                    : "border-white/15 text-white/60 hover:border-white hover:text-white"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
            <div className="relative w-full sm:w-auto">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="catalog-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Hledat…"
                className="bg-[#111] border border-[#222] pl-9 pr-4 py-2.5 text-sm w-full lg:w-56 focus:outline-none focus:ring-1 focus:ring-[#ff3b30]"
              />
            </div>
            {category === "pneu" ? (
              <>
                <select
                  data-testid="filter-tyre-brand"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  className="w-full sm:w-auto bg-[#111] border border-[#222] px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff3b30]"
                >
                  <option value="">Všechny značky</option>
                  {TYRE_BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
                <select
                  data-testid="filter-size"
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                  className="w-full sm:w-auto bg-[#111] border border-[#222] px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff3b30]"
                >
                  <option value="">Všechny rozměry</option>
                  {SIZES.map((s) => <option key={s} value={s}>{`R${s}″`}</option>)}
                </select>
              </>
            ) : category === "dily" ? null : (
              <select
                data-testid="filter-brand"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="w-full sm:w-auto bg-[#111] border border-[#222] px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff3b30]"
              >
                <option value="">Všechny značky</option>
                {BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            )}
          </div>
        </div>

        {loading ? (
          <div className="py-32 text-center font-display text-2xl text-white/30 animate-pulse">Načítání…</div>
        ) : listings.length === 0 ? (
          <div data-testid="catalog-empty" className="py-32 text-center">
            <p className="font-display text-3xl text-white/40 mb-3">Žádné inzeráty</p>
            <p className="text-white/50">Zkuste upravit filtry nebo hledaný výraz.</p>
          </div>
        ) : (
          <div data-testid="catalog-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
